import type { PeriodoSemanal } from '@adelante/shared';
import { CalendarBlank, CaretDown, CaretRight, Check, Minus } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Selector jerárquico Año › Mes › Semana ISO con checkboxes tri-estado, EXCLUSIVO
 * de las pantallas Avance y Venta/Indirectos. A diferencia del filtro global de
 * Períodos (que trabaja con días y los traduce), éste trabaja directamente con
 * SEMANAS ISO reales (S23, S24…) que vienen de las tablas _semanal vía
 * /api/periodos-semanales — la misma fuente que alimenta las columnas. Así lo que
 * se marca es exactamente lo que existe y lo que se muestra, sin traducciones.
 *
 * Estado emitido: un array de claves "anio-mes-semanaIso" (ej. "2026-6-23").
 * Permite marcar semanas de meses/años distintos para comparar.
 */

const NOMBRES_MES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

type Estado = 'all' | 'some' | 'none';

/** Clave canónica de una semana: "anio-mes-semanaIso" (ej. "2026-6-23"). */
export function claveSemana(anio: number, mes: number, semanaIso: number): string {
  return `${anio}-${mes}-${semanaIso}`;
}

interface Props {
  /** Períodos semanales disponibles (de /api/periodos-semanales). */
  disponibles: PeriodoSemanal[];
  /** Semanas seleccionadas (claves "anio-mes-semanaIso"). */
  seleccion: string[];
  onChange: (semanas: string[]) => void;
}

export function FiltroPeriodosSemanal({ disponibles, seleccion, onChange }: Props) {
  const [abierto, setAbierto] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  const sel = useMemo(() => new Set(seleccion), [seleccion]);

  // Catálogo: años (desc), meses por año (asc), semanas por mes (asc), todas
  // las claves de semana por año/mes (para el tri-estado de los niveles).
  const cat = useMemo(() => {
    const anios: number[] = [];
    const mesesPorAnio = new Map<number, number[]>();
    const semanasPorMes = new Map<string, number[]>(); // "anio-mes" -> [semanaIso...]
    const clavesPorAnio = new Map<number, string[]>();
    const clavesPorMes = new Map<string, string[]>();

    const orden = [...disponibles].sort((a, b) =>
      a.anio !== b.anio
        ? a.anio - b.anio
        : a.mes !== b.mes
          ? a.mes - b.mes
          : a.semanaIso - b.semanaIso,
    );
    for (const p of orden) {
      const mk = `${p.anio}-${p.mes}`;
      const clave = claveSemana(p.anio, p.mes, p.semanaIso);
      if (!mesesPorAnio.has(p.anio)) {
        mesesPorAnio.set(p.anio, []);
        clavesPorAnio.set(p.anio, []);
        anios.push(p.anio);
      }
      const mlist = mesesPorAnio.get(p.anio)!;
      if (!mlist.includes(p.mes)) mlist.push(p.mes);
      if (!semanasPorMes.has(mk)) semanasPorMes.set(mk, []);
      if (!semanasPorMes.get(mk)!.includes(p.semanaIso)) semanasPorMes.get(mk)!.push(p.semanaIso);
      clavesPorAnio.get(p.anio)!.push(clave);
      if (!clavesPorMes.has(mk)) clavesPorMes.set(mk, []);
      clavesPorMes.get(mk)!.push(clave);
    }
    anios.sort((a, b) => b - a);
    return { anios, mesesPorAnio, semanasPorMes, clavesPorAnio, clavesPorMes };
  }, [disponibles]);

  const estadoDe = (claves: string[]): Estado => {
    if (claves.length === 0) return 'none';
    let n = 0;
    for (const c of claves) if (sel.has(c)) n++;
    return n === 0 ? 'none' : n === claves.length ? 'all' : 'some';
  };

  const emitir = (nuevo: Set<string>) => onChange([...nuevo]);

  const conClaves = (claves: string[], incluir: boolean): Set<string> => {
    const s = new Set(sel);
    if (incluir) for (const c of claves) s.add(c);
    else for (const c of claves) s.delete(c);
    return s;
  };

  const toggleAnio = (anio: number) => {
    const claves = cat.clavesPorAnio.get(anio) ?? [];
    emitir(conClaves(claves, estadoDe(claves) !== 'all'));
  };
  const toggleMes = (anio: number, mes: number) => {
    const claves = cat.clavesPorMes.get(`${anio}-${mes}`) ?? [];
    emitir(conClaves(claves, estadoDe(claves) !== 'all'));
  };
  const toggleSemana = (anio: number, mes: number, semanaIso: number) => {
    const clave = claveSemana(anio, mes, semanaIso);
    emitir(conClaves([clave], !sel.has(clave)));
  };

  const toggleExpand = (key: string) =>
    setExpandidos((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const limpiar = () => onChange([]);

  const resumen = (() => {
    if (sel.size === 0) return 'Todas las semanas';
    if (sel.size === 1) {
      const [a, m, w] = [...sel][0]!.split('-').map(Number);
      return `${NOMBRES_MES[(m ?? 1) - 1]?.slice(0, 3)} S${w} ${a}`;
    }
    return `${sel.size} semanas`;
  })();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className={`flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition ${
          sel.size > 0
            ? 'border-blue-400 bg-blue-50 text-blue-700'
            : 'border-input bg-background text-foreground hover:bg-zinc-50'
        }`}
        title="Seleccionar semanas (Año/Mes/Semana ISO). Exclusivo de esta pantalla."
      >
        <CalendarBlank className="h-3.5 w-3.5" weight="bold" />
        Semanas
        <span className="max-w-[160px] truncate text-muted-foreground">{resumen}</span>
        <CaretDown className="h-3 w-3" weight="bold" />
      </button>

      {abierto && (
        <div className="absolute left-0 z-50 mt-1 w-64 rounded-md border border-zinc-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2 text-xs">
            <span className="font-medium text-muted-foreground">Año / Mes / Semana ISO</span>
            <button type="button" onClick={limpiar} className="text-blue-600 hover:underline">
              Limpiar
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto py-1">
            {cat.anios.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">Sin semanas.</p>
            ) : (
              cat.anios.map((anio) => {
                const anioExp = expandidos.has(String(anio));
                return (
                  <div key={anio}>
                    <Fila
                      nivel={0}
                      estado={estadoDe(cat.clavesPorAnio.get(anio) ?? [])}
                      label={String(anio)}
                      expandible
                      expandido={anioExp}
                      onToggleExpand={() => toggleExpand(String(anio))}
                      onToggleCheck={() => toggleAnio(anio)}
                    />
                    {anioExp &&
                      (cat.mesesPorAnio.get(anio) ?? []).map((mes) => {
                        const mk = `${anio}-${mes}`;
                        const mesExp = expandidos.has(mk);
                        return (
                          <div key={mk}>
                            <Fila
                              nivel={1}
                              estado={estadoDe(cat.clavesPorMes.get(mk) ?? [])}
                              label={NOMBRES_MES[mes - 1] ?? String(mes)}
                              expandible
                              expandido={mesExp}
                              onToggleExpand={() => toggleExpand(mk)}
                              onToggleCheck={() => toggleMes(anio, mes)}
                            />
                            {mesExp &&
                              (cat.semanasPorMes.get(mk) ?? []).map((w) => (
                                <Fila
                                  key={`${mk}-${w}`}
                                  nivel={2}
                                  estado={sel.has(claveSemana(anio, mes, w)) ? 'all' : 'none'}
                                  label={`Semana ${w}`}
                                  expandible={false}
                                  expandido={false}
                                  onToggleExpand={() => {}}
                                  onToggleCheck={() => toggleSemana(anio, mes, w)}
                                />
                              ))}
                          </div>
                        );
                      })}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Fila({
  nivel,
  estado,
  label,
  expandible,
  expandido,
  onToggleExpand,
  onToggleCheck,
}: {
  nivel: number;
  estado: Estado;
  label: string;
  expandible: boolean;
  expandido: boolean;
  onToggleExpand: () => void;
  onToggleCheck: () => void;
}) {
  return (
    <div
      className="flex items-center gap-1 px-3 py-1 text-xs hover:bg-zinc-50"
      style={{ paddingLeft: `${12 + nivel * 16}px` }}
    >
      {expandible ? (
        <button
          type="button"
          onClick={onToggleExpand}
          className="rounded p-0.5 hover:bg-zinc-200"
          title={expandido ? 'Colapsar' : 'Expandir'}
        >
          {expandido ? (
            <CaretDown className="h-3 w-3" weight="bold" />
          ) : (
            <CaretRight className="h-3 w-3" weight="bold" />
          )}
        </button>
      ) : (
        <span className="w-4" />
      )}
      <button type="button" onClick={onToggleCheck} className="flex items-center gap-1.5">
        <span
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
            estado === 'none'
              ? 'border-zinc-300 bg-white'
              : 'border-blue-600 bg-blue-600 text-white'
          }`}
        >
          {estado === 'all' && <Check className="h-3 w-3" weight="bold" />}
          {estado === 'some' && <Minus className="h-3 w-3" weight="bold" />}
        </span>
        <span className={nivel === 0 ? 'font-medium' : ''}>{label}</span>
      </button>
    </div>
  );
}
